/**
 * SlideoutHeaderActions — the unified action bar for the slideout record header.
 *
 * Merges the entity-level CTAs (Draft email, Add note, Add to CRM, …) with the
 * record's overflow actions (Research, Enrich, Delete) into a SINGLE
 * ResponsiveActionBar:
 *   - Desktop: entity CTAs render as inline buttons; record actions live in one
 *     kebab dropdown to their right.
 *   - Mobile: everything collapses into that same single dropdown — no second
 *     kebab. (This is the fix for the double-dropdown bug.)
 *
 * Record-action logic (and the delete modal) comes from the shared
 * `useRecordActions` hook, so the slideout and full-page surfaces stay aligned.
 * For deal records, `useDealStageSubmenus` also feeds "Move stage" and
 * "Change pipeline" submenus into that same dropdown.
 */

import { useMemo } from 'react';

import type { ResponsiveAction } from '@/lib/base/buttons';
import { ResponsiveActionBar } from '@/lib/base/buttons';
import type { CrmObject } from '@/lib/crm/core/types';

import { useDealStageSubmenus } from './useDealStageSubmenus';
import { useRecordActions } from './useRecordActions';

interface SlideoutHeaderActionsProps {
  object: CrmObject;
  /** Entity-level CTAs rendered inline on desktop, collapsed on mobile. */
  entityActions: ResponsiveAction[];
}

export function SlideoutHeaderActions({
  object,
  entityActions,
}: SlideoutHeaderActionsProps) {
  // hideLifecycleStage: the slideout does not offer a lifecycle-stage set
  // affordance in its header chrome. The header's dedicated stage button was
  // removed as unnecessary; a contact's lifecycle stage is set by editing the
  // record's Lifecycle Stage field like any other association.
  const { simpleActions, deleteModal, mergeElements } = useRecordActions({
    object,
    entityTypeName: object.entity_type_name,
    viewContext: 'slideout',
    hideLifecycleStage: true,
  });

  const dealSubmenus = useDealStageSubmenus({
    object,
    entityTypeName: object.entity_type_name,
  });

  // Entity CTAs are inline (collapse on mobile); record actions stay in the
  // dropdown (simpleActions already carry placement:'dropdown').
  const actions = useMemo<ResponsiveAction[]>(
    () => [...entityActions, ...simpleActions],
    [entityActions, simpleActions]
  );

  return (
    <>
      <ResponsiveActionBar
        actions={actions}
        submenuActions={dealSubmenus}
        dropdownLabel='Record actions'
        inlineFrom='md'
      />
      {deleteModal}
      {mergeElements}
    </>
  );
}
